/**
 * @license
 * Lodash (Custom Build) <https://lodash.com/>
 * Build: `lodash include="debounce" --development`
 * Copyright JS Foundation and other contributors <https://js.foundation/>
 * Released under MIT license <https://lodash.com/license>
 * Based on Underscore.js 1.8.3 <http://underscorejs.org/LICENSE>
 * Copyright Jeremy Ashkenas, DocumentCloud and Investigative Reporters & Editors
 */
export function debounce(func: Function, waitTime: number = 0) {
	let leading = false;
	let trailing = true;
	let lastInvokeTime = 0;
	let lastCallTime: number;
	let result: any = undefined;
	let lastThis: any = undefined;
	let timerID: number | undefined = undefined;
	let lastArgs: IArguments | undefined = undefined;

	function cancel() {
		if (timerID !== undefined) {
			clearTimeout(timerID);
		}
		lastArgs = undefined;
		lastCallTime = 0;
		lastInvokeTime = 0;
		lastThis = undefined;
		timerID = undefined;
	}

	function flush() {
		return timerID === undefined
			? result
			: trailingEdge(Date.now());
	}

	function invokeFunc(time: number) {
		const args = lastArgs;
		const thisArg = lastThis;
		lastArgs = undefined;
		lastThis = undefined;
		lastInvokeTime = time;
		result = func.apply(thisArg, args);
		return result;
	}

	function leadingEdge(time: number) {
		lastInvokeTime = time;
		timerID = setTimeout(timerExpired, waitTime);
		return leading
			? invokeFunc(time)
			: result;
	}

	function remainingWait(time: number): number {
		return waitTime - (time - lastCallTime);
	}

	function shouldInvoke(time: number): boolean {
		const timeSinceLastCall = time - lastCallTime;
		return (lastCallTime === undefined)
			|| (timeSinceLastCall >= waitTime)
			|| (timeSinceLastCall < 0);
	}

	function timerExpired() {
		const time = Date.now();
		if (shouldInvoke(time)) {
			return trailingEdge(time);
		}
		timerID = setTimeout(timerExpired, remainingWait(time));
	}

	function trailingEdge(time: number) {
		timerID = undefined;
		if (trailing && lastArgs) {
			return invokeFunc(time);
		}
		lastArgs = undefined;
		lastThis = undefined;
		return result;
	}

	function debounced(this: any) {
		const time = Date.now();
		const isInvoking = shouldInvoke(time);
		lastArgs = arguments;
		lastThis = this;
		lastCallTime = time;
		if (isInvoking) {
			if (timerID === undefined) {
				return leadingEdge(lastCallTime);
			}
		}
		if (timerID === undefined) {
			timerID = setTimeout(timerExpired, waitTime);
		}
		return result;
	}

	debounced.cancel = cancel;
	debounced.flush = flush;
	return debounced;
}
